import Box from '@mui/material/Box';
import { cloneDeep } from 'lodash-es';
import { Fragment, type JSX, useState } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { Button } from 'Components/Buttons/Buttons';
import { IconButton } from 'Components/Buttons/IconButton';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FormFieldMultiOptionSelect } from 'Components/FormField/FormFieldMultiOptionSelect/FormFieldMultiOptionSelect';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { FormFieldTextAreaAi } from 'Components/FormField/FormFieldTextAreaAi/FormFieldTextAreaAi';
import { FormFieldUserMultiSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserMultiSelect';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { Text } from 'Components/Text/Text';
import { UnsavedChangesWarning } from 'Components/Text/UnsavedChangesWarning';
import { ICON_DELETE_REMOVE, ICON_SAVE } from 'Config/Icons';
import { TPRM_THIRD_PARTY_SERVICE_DELEGATES, TPRM_THIRD_PARTY_SERVICE_DESCRIPTION, TPRM_THIRD_PARTY_SERVICE_MANAGER, TPRM_THIRD_PARTY_SERVICE_NAME, TPRM_THIRD_PARTY_SERVICE_RESPONSIBLE_ORGANIZATION } from 'Config/Tooltips';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { validateEmail } from 'Helpers/InputValidation';
import { TPRM_ROLES } from 'Helpers/UserUtils';
import { useAiContentAdvisor } from 'Hooks/AiContentAdvisor';
import { useUnsavedChangesTracking } from 'Hooks/Forms';
import { AiContentAdvisorFeature } from 'Models/ArtificialIntelligence';
import { ValidationError } from 'Models/ErrorTypes';
import { FinalizationState } from 'Models/Organization';
import { CreateServiceRequest, ServiceForNonTprmUser, ThirdPartyContact, ThirdPartyForNonTprmUser, ThirdPartyResponse, ThirdPartyService, UpdateServiceRequest } from 'Models/TPRM';
import { GroupOptionType } from 'Models/Types/GlobalType';
import { UserResponse } from 'Models/User';

import styles from './ManageThirdPartyServiceFormFields.module.css';
import { DeleteThirdPartyServiceModal } from '../DeleteThirdPartyServiceModal/DeleteThirdPartyServiceModal';

export interface ManageThirdPartyServiceFormFieldsProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    onFormSubmitBegin: () => void;
    onFormSubmitFailed: (errorMessage: string) => void;
    onFormSubmittedSuccessfully: (service: ThirdPartyService | ServiceForNonTprmUser) => Promise<void>;
    thirdParty: ThirdPartyResponse | ThirdPartyForNonTprmUser;
    tprmApi: ClientTPRMApi;
    userHasFullTprmAccess: boolean;

    /** If `true`, the user will not be able to delete the third-party service. At the time of writing, this is `true` only for the TPRM Intake page. */
    hideDeleteButton?: boolean;
    service?: ThirdPartyService | ServiceForNonTprmUser;
}

interface FormFieldsState {
    name?: string;
    description?: string;
    thirdPartyServiceManagerUserId?: string;
    delegates?: UserResponse[];
    tags: string[];
    third_party_contacts: ThirdPartyContact[];
    responsibleOrganization?: string;
}

type RequestActionNoConfirmationNeeded = 'edit' | 'createAsDraft';
type RequestActionConfirmationNeeded = 'delete';

type RequestAndModalState =
    | {
          requestState: 'idle';
          deleteConfirmationModalIsActive: boolean;
      }
    | {
          requestState: 'inProgress';
          action: RequestActionNoConfirmationNeeded | RequestActionConfirmationNeeded;
      }
    | {
          requestState: 'success';
          completedAction: RequestActionNoConfirmationNeeded;
          deleteConfirmationModalIsActive: false;
      }
    | {
          requestState: 'success';
          completedAction: RequestActionConfirmationNeeded;
          deleteConfirmationModalIsActive: boolean;
      }
    | {
          requestState: 'failure';
      };

/**
 * Renders a form for creating a new third-party service, or updating or deleting an existing third-party service.
 * Used on the Manage Third-Party Service page as well as on the TPRM Intake page.
 */
export const ManageThirdPartyServiceFormFields = (props: ManageThirdPartyServiceFormFieldsProps): JSX.Element => {
    const cachedData = useCachedData();

    const [requestAndModalState, setRequestAndModalState] = useState<RequestAndModalState>({ requestState: 'idle', deleteConfirmationModalIsActive: false });
    const [serviceManager, setServiceManager] = useState<UserResponse | undefined>(cachedData.users.find((user) => user.cognito_subject === props.service?.vendor_service_manager_user_id)); // Keeps track of the currently selected User in the Third-Party Manager drop-down menu.

    const [formFieldsState, setFormFieldsState] = useState<FormFieldsState>({
        name: props.service?.name,
        description: props.service?.description,
        thirdPartyServiceManagerUserId: props.service?.vendor_service_manager_user_id,
        delegates: props.service?.delegates ? cachedData.users.filter((user) => props.service?.delegates?.includes(user.cognito_subject)) : undefined,
        tags: props.service?.tags ?? [],
        third_party_contacts: props.service?.vendor_contacts ?? [],
        responsibleOrganization: props.service?.responsible_organization,
    });

    const [hasUnsavedChanges, onFormFieldValuesSaved] = useUnsavedChangesTracking(formFieldsState, false);

    const descriptionAi = useAiContentAdvisor(props.artificialIntelligenceApi, formFieldsState.description ?? '', AiContentAdvisorFeature.TPRM_SERVICE_DESCRIPTION, props.onFormSubmitFailed);

    const createThirdPartyService = async (irqTargetCompletionDate?: Date): Promise<void> => {
        setRequestAndModalState({ requestState: 'inProgress', action: 'createAsDraft' });

        try {
            validateForm();
            props.onFormSubmitBegin();
            const service = (await props.tprmApi.createThirdPartyService(buildCreateRequest(irqTargetCompletionDate), props.thirdParty.id)).data;
            await props.onFormSubmittedSuccessfully(service);
            setRequestAndModalState({ requestState: 'success', completedAction: 'createAsDraft', deleteConfirmationModalIsActive: false });
            onFormFieldValuesSaved(formFieldsState);
        } catch (err) {
            handleRequestError(err);
        }
    };

    const updateThirdPartyService = async (): Promise<void> => {
        setRequestAndModalState({ requestState: 'inProgress', action: 'edit' });

        try {
            validateForm();
            props.onFormSubmitBegin();
            const service = (await props.tprmApi.updateThirdPartyService(buildUpdateRequest(), props.thirdParty.id, props.service!.id)).data;
            await props.onFormSubmittedSuccessfully(service);
            setRequestAndModalState({ requestState: 'success', completedAction: 'edit', deleteConfirmationModalIsActive: false });
            onFormFieldValuesSaved(formFieldsState);
        } catch (err) {
            handleRequestError(err);
        }
    };

    const buildCreateRequest = (irqTargetCompletionDate?: Date): CreateServiceRequest => {
        const request: CreateServiceRequest = {
            finalization_state: FinalizationState.DRAFT_IN_PROGRESS,
            name: formFieldsState.name!,
            description: formFieldsState.description!,
            vendor_service_manager_user_id: formFieldsState.thirdPartyServiceManagerUserId!,
            assessment_due_date: props.service?.assessment_workflow_setup?.due_date,
            delegates: formFieldsState.delegates?.map((user) => user.cognito_subject),
            irq_target_completion_date: irqTargetCompletionDate ? jsDateToIso8601(irqTargetCompletionDate) : undefined,
            responsible_organization: formFieldsState.responsibleOrganization,
            tags: formFieldsState.tags,
            vendor_contacts: cloneDeep(formFieldsState.third_party_contacts), // cloneDeep() is used so that we can set empty strings to undefined in the next step without modifying the original state, which would throw a React error about switching from controlled to uncontrolled components.
        };

        // Discard any Third-Party contacts where all attributes are "empty."
        // This is necessary because the API will save empty Third-Party contacts to the database (as {}) and then empty Third-Party contact "rows" will persist in the UI.
        request.vendor_contacts = request.vendor_contacts?.filter((thirdPartyContact) => {
            return Object.values(thirdPartyContact).some((value) => {
                if (typeof value === 'string' && value.trim().length === 0) {
                    return false;
                }
                return true;
            });
        });

        // Set any attributes of Third-Party contacts that are "empty" strings to undefined before submitting the request.
        // This is necessary because the API will return an error if email_address is an empty string. The other attributes are included simply for completeness.
        request.vendor_contacts?.forEach((thirdPartyContact) => {
            if ((thirdPartyContact.email_address?.trim().length ?? 0) === 0) {
                thirdPartyContact.email_address = undefined;
            }
            if ((thirdPartyContact.name?.trim().length ?? 0) === 0) {
                thirdPartyContact.name = undefined;
            }
            if ((thirdPartyContact.phone_number?.trim().length ?? 0) === 0) {
                thirdPartyContact.phone_number = undefined;
            }
            if ((thirdPartyContact.additional_information?.trim().length ?? 0) === 0) {
                thirdPartyContact.additional_information = undefined;
            }
        });

        return request;
    };

    const buildUpdateRequest = (): UpdateServiceRequest => {
        const request: UpdateServiceRequest = {
            name: formFieldsState.name!,
            description: formFieldsState.description!,
            vendor_service_manager_user_id: formFieldsState.thirdPartyServiceManagerUserId!,
            assessment_due_date: props.service?.assessment_workflow_setup?.due_date,
            delegates: formFieldsState.delegates?.map((user) => user.cognito_subject),
            irq_target_completion_date: props.service?.assessment_workflow_setup?.irq_target_completion_date,
            responsible_organization: formFieldsState.responsibleOrganization,
            tags: formFieldsState.tags,
            vendor_contacts: cloneDeep(formFieldsState.third_party_contacts), // cloneDeep() is used so that we can set empty strings to undefined in the next step without modifying the original state, which would throw a React error about switching from controlled to uncontrolled components.
        };

        // Discard any Third-Party contacts where all attributes are "empty."
        // This is necessary because the API will save empty Third-Party contacts to the database (as {}) and then empty Third-Party contact "rows" will persist in the UI.
        request.vendor_contacts = request.vendor_contacts?.filter((thirdPartyContact) => {
            return Object.values(thirdPartyContact).some((value) => {
                if (typeof value === 'string' && value.trim().length === 0) {
                    return false;
                }
                return true;
            });
        });

        // Set any attributes of Third-Party contacts that are "empty" strings to undefined before submitting the request.
        // This is necessary because the API will return an error if email_address is an empty string. The other attributes are included simply for completeness.
        request.vendor_contacts?.forEach((thirdPartyContact) => {
            if ((thirdPartyContact.email_address?.trim().length ?? 0) === 0) {
                thirdPartyContact.email_address = undefined;
            }
            if ((thirdPartyContact.name?.trim().length ?? 0) === 0) {
                thirdPartyContact.name = undefined;
            }
            if ((thirdPartyContact.phone_number?.trim().length ?? 0) === 0) {
                thirdPartyContact.phone_number = undefined;
            }
            if ((thirdPartyContact.additional_information?.trim().length ?? 0) === 0) {
                thirdPartyContact.additional_information = undefined;
            }
        });

        return request;
    };

    const validateForm = (): void => {
        if (!formFieldsState.name) {
            throw new ValidationError('Name is required.');
        }
        if (!formFieldsState.thirdPartyServiceManagerUserId) {
            throw new ValidationError('Manager is required.');
        }
        if (!formFieldsState.description) {
            throw new ValidationError('Description is required.');
        }
        formFieldsState.third_party_contacts.forEach((third_party_contact) => {
            if (third_party_contact.email_address && !validateEmail(third_party_contact.email_address)) {
                throw new ValidationError('Invalid email address.');
            }
        });
    };

    /** Handles error responses from the API. */
    const handleRequestError = (error: Error): void => {
        props.onFormSubmitFailed(error.message);
        setRequestAndModalState({ requestState: 'failure' });
    };

    /** Handles changes made to general text fields. */
    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        setFormFieldsState({ ...formFieldsState, [event.currentTarget.name]: event.currentTarget.value });
    };

    /** Handles changes made to the Third-Party Service Manager drop-down (select) field. */
    const handleSelectUserChange = (user: UserResponse | undefined, formFieldId: string): void => {
        if (user === undefined) {
            setServiceManager(undefined);
            setFormFieldsState({ ...formFieldsState, [formFieldId]: undefined });
        } else {
            setServiceManager(user);
            setFormFieldsState({ ...formFieldsState, [formFieldId]: user.cognito_subject });
        }
    };

    /** Handles changes made to the Third-Party Service Delegates drop-down (select) field. */
    const handleSelectUsersChange = (users: UserResponse[] | undefined, formFieldId: string): void => {
        setFormFieldsState({ ...formFieldsState, [formFieldId]: users });
    };

    /** Adds the fields (empty) for a new Third-Party contact. */
    const handleAddThirdPartyContact = (): void => {
        const thirdPartyContacts = [...formFieldsState.third_party_contacts];
        thirdPartyContacts.push({ name: '', email_address: '', phone_number: '', additional_information: '' });
        setFormFieldsState({ ...formFieldsState, third_party_contacts: thirdPartyContacts });
    };

    /** Deletes an existing Third-Party contact. */
    const handleDeleteThirdPartyContact = (index: number): void => {
        const thirdPartyContacts = [...formFieldsState.third_party_contacts];
        thirdPartyContacts.splice(index, 1);
        setFormFieldsState({ ...formFieldsState, third_party_contacts: thirdPartyContacts });
    };

    /** Handles changes made to an existing Third-Party contact. */
    const handleThirdPartyContactChange = (index: number, attribute: 'name' | 'email_address' | 'phone_number' | 'additional_information', value: string): void => {
        const thirdPartyContacts = [...formFieldsState.third_party_contacts];
        thirdPartyContacts[index] = {
            ...thirdPartyContacts[index],
            [attribute]: value,
        };
        setFormFieldsState({ ...formFieldsState, third_party_contacts: thirdPartyContacts });
    };

    const service = props.service;
    const formDisabled = requestAndModalState.requestState === 'inProgress' || (requestAndModalState.requestState === 'success' && requestAndModalState.completedAction === 'delete');

    return (
        <>
            {(requestAndModalState.requestState === 'idle' || requestAndModalState.requestState === 'success') && requestAndModalState.deleteConfirmationModalIsActive && service && <DeleteThirdPartyServiceModal hideModal={() => setRequestAndModalState({ ...requestAndModalState, deleteConfirmationModalIsActive: false })} onSuccess={async () => setRequestAndModalState({ requestState: 'success', completedAction: 'delete', deleteConfirmationModalIsActive: true })} service={service} tprmApi={props.tprmApi} userHasFullTprmAccess={props.userHasFullTprmAccess} />}
            <Box component="form" noValidate>
                <fieldset disabled={formDisabled}>
                    <div className={styles.formFieldGroup}>
                        <div className={styles.formFieldContainer}>
                            <FormFieldText formFieldType="text" handleChange={handleChange} formFieldId="name" formFieldLabel="Name" required tooltip={TPRM_THIRD_PARTY_SERVICE_NAME} value={formFieldsState.name || ''} />
                        </div>
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldText formFieldType="text" handleChange={handleChange} formFieldId="responsibleOrganization" formFieldLabel="Organization Responsible for Third-Party Relationship" tooltip={TPRM_THIRD_PARTY_SERVICE_RESPONSIBLE_ORGANIZATION} value={formFieldsState.responsibleOrganization || ''} />
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldUserSelect rolesRestriction={TPRM_ROLES} onUserSelected={handleSelectUserChange} formFieldId="thirdPartyServiceManagerUserId" selectedUser={serviceManager} formFieldLabel="Manager" tooltip={TPRM_THIRD_PARTY_SERVICE_MANAGER} required disabled={formDisabled} />
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldUserMultiSelect rolesRestriction={TPRM_ROLES} onUsersSelected={handleSelectUsersChange} formFieldId="delegates" selectedUsers={formFieldsState.delegates} formFieldLabel="Delegates" tooltip={TPRM_THIRD_PARTY_SERVICE_DELEGATES} disabled={formDisabled} />
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldTextAreaAi handleChange={handleChange} formFieldId="description" formFieldLabel="Description" rows={5} required tooltip={TPRM_THIRD_PARTY_SERVICE_DESCRIPTION} value={formFieldsState.description || ''} aiContentAdvisorValues={descriptionAi} />
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldMultiOptionSelect
                            defaultSelectedOptions={cachedData
                                .getTagCategoriesGroupedOptions()
                                .map((group) => group.options)
                                .flat()
                                .filter((option) => formFieldsState.tags.includes(option.value as string))}
                            formFieldLabel="Tags"
                            formFieldId="tags"
                            handleChange={(value: GroupOptionType[]) => setFormFieldsState((current) => ({ ...current, tags: value.map((tag) => tag.value as string) }))}
                            options={cachedData.getTagCategoriesGroupedOptions()}
                            accessibilityLabel="Tags"
                            disabled={formDisabled}
                        />
                    </div>
                    <Text variant="Header3" color="darkGray">
                        Third-Party Service Contact Information
                    </Text>
                    {formFieldsState.third_party_contacts.map((thirdPartyContact, index) => (
                        <Fragment key={index}>
                            <div className={styles.formFieldGroup}>
                                <div className={styles.formFieldContainer}>
                                    <FormFieldText formFieldType="text" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'name', event.currentTarget.value)} formFieldId={`thirdPartyContactName${index}`} formFieldLabel={`Name`} tooltip="The name of the contact at this third party for this service." value={thirdPartyContact.name || ''} />
                                </div>
                                <div className={styles.formFieldContainer}>
                                    <FormFieldText formFieldType="email" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'email_address', event.currentTarget.value)} formFieldId={`thirdPartyContactEmailAddress${index}`} formFieldLabel={`Email Address`} tooltip="The email address of the contact at this third party for this service." value={thirdPartyContact.email_address || ''} />
                                </div>
                                <div className={styles.formFieldContainer}>
                                    <FormFieldText formFieldType="text" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'phone_number', event.currentTarget.value)} formFieldId={`thirdPartyContactPhoneNumber${index}`} formFieldLabel={`Phone Number`} tooltip="The phone number of the contact at this third party for this service." value={thirdPartyContact.phone_number || ''} />
                                </div>
                                <div className={styles.trashIconContainer}>
                                    <IconButton aria-label={`delete third-party contact ${index}`} onClick={() => handleDeleteThirdPartyContact(index)} fontAwesomeImage={ICON_DELETE_REMOVE} />
                                </div>
                            </div>
                            <div className={styles.formFieldContainer}>
                                <FormFieldTextArea handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'additional_information', event.currentTarget.value)} formFieldId={`thirdPartyContactAdditionalInformation${index}`} formFieldLabel={`Additional Information`} tooltip="Any additional information for the contact at this third party." value={thirdPartyContact.additional_information || ''} />
                            </div>
                        </Fragment>
                    ))}
                    <div className={styles.formFieldContainerBottom}>
                        <Button variant="linkText" size="lg" onClick={handleAddThirdPartyContact}>
                            {'+ Add Contact'}
                        </Button>
                    </div>
                    <div className={'modalFormButtonContainer'}>
                        <>
                            {props.service && !props.hideDeleteButton && (
                                <div className={styles.deleteButton}>
                                    <Button fontAwesomeImage={ICON_DELETE_REMOVE} onClick={() => setRequestAndModalState({ requestState: 'idle', deleteConfirmationModalIsActive: true })} variant="danger" isLoading={false} loadingText="Deleting...">
                                        Delete
                                    </Button>
                                </div>
                            )}
                            <Button
                                variant="primary"
                                fontAwesomeImage={ICON_SAVE}
                                isLoading={requestAndModalState.requestState === 'inProgress' && requestAndModalState.action === (props.service ? 'edit' : 'createAsDraft')}
                                loadingText="Saving..."
                                onClick={() => {
                                    if (props.service) {
                                        updateThirdPartyService();
                                    } else {
                                        createThirdPartyService();
                                    }
                                }}
                            >
                                Save
                            </Button>
                        </>
                    </div>
                    <UnsavedChangesWarning hasUnsavedChanges={hasUnsavedChanges} />
                </fieldset>
            </Box>
        </>
    );
};
